import {
  Box,
  Button,
  Grid,
  GridItem,
  Heading,
  Tag,
  TagProps,
  Text,
  theme,
} from '@everlywell/ui-kit';
import DropdownFilter from 'components/DropdownFilter';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import PriceV2 from 'components/PricingV2/PriceV2';
import ProductCardV2 from 'components/ProductCardV2';
import { Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import React, { useEffect, useState } from 'react';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { ProductDataType } from 'utils/types';
import { Metadata } from 'utils/types/contentfulTypes';

export type ProductTierSectionProps = {
  displayName?: string;
  metadata?: Metadata;
  productsWithSpreeData?: Record<string, ProductDataType>;
};

type CreditTier = {
  label: string;
  value: number;
};

export type ProductWithCost = {
  slug?: string;
  creditCost?: number;
  tag?: string;
  // If the tagVariant is not found it defaults to primary when there is a tag
  tagVariant?: string;
};

export type Context = {
  creditTiers: CreditTier[];
  products: ProductWithCost[];
  defaultCategory?: string;
};

/**
 * Removes inexistent categories from the list of products and orders them
 * @param productsWithCost - The list of products with cost
 * @param productsWithSpreeData - The list of products with spree data
 * @param productCategories - The list of product categories
 * @returns The list of categories
 *
 * Use case:
 * This is needed because for a given MDP we might not offer products for all categories.
 * We need to remove the categories that don't have products from the list of categories.
 *
 * After that, we need to order the categories by the number of products.
 */
const removeInexistentCategoriesAndOrder = (
  productsWithCost: ProductWithCost[],
  productsWithSpreeData: Record<string, ProductDataType> | undefined,
): string[] => {
  const categoriesWithCount: Record<string, number> = {};

  productsWithCost.forEach(({ slug }) => {
    const product =
      productsWithSpreeData && slug && productsWithSpreeData[slug];

    if (!product || !product.category || !product.categories) {
      return;
    }

    const currentCategories = [product.category, ...product.categories];

    currentCategories.forEach((category) => {
      // If the category is not in the list, add it
      if (!categoriesWithCount[category]) {
        categoriesWithCount[category] = 1;
        return;
      }

      // If the category is in the list, increment the count
      categoriesWithCount[category]++;
    });
  });

  //Order the categories by the number of products
  const orderedCategories = Object.keys(categoriesWithCount).sort(
    (a, b) => categoriesWithCount[b] - categoriesWithCount[a],
  );

  return orderedCategories;
};

const INITIALLY_VISIBLE_AMOUNT = 8;

/**
 * Renders the ProductTierSection component
 * A list of products with their credit value and a filter
 *
 * @param {ProductTierSectionProps} props - The props for the component
 */
export default function ProductTierSection({
  displayName,
  metadata,
  productsWithSpreeData,
}: ProductTierSectionProps): JSX.Element {
  const { context: rawContext, description, title, flags } = metadata || {};

  const context = rawContext as Context;
  const { creditTiers, products: productsWithCost, defaultCategory } = context;
  const creditLabels = ['Any', ...creditTiers.map((tier) => tier.label)];
  // Look for the flag that enables the PDP navigation
  const hasPdpNavigation = flags?.find(
    (flag) => flag.displayName === 'hasPdpNavigation' && flag.value === true,
  );

  const availableCategories = removeInexistentCategoriesAndOrder(
    productsWithCost,
    productsWithSpreeData,
  );
  const categories = ['All', ...(availableCategories ?? [])];
  const numberOfProducts = productsWithCost.length;

  const [selectedCreditTier, setSelectedCreditTier] = useState<string>('Any');
  const [selectedCategory, setSelectedCategory] = useState<string>(
    defaultCategory || 'All',
  );
  // Visible products are the products that are currently being displayed
  const [visibleProducts, setVisibleProducts] =
    useState<ProductWithCost[]>(productsWithCost);

  const [remainingCount, setRemainingCount] = useState<number>(
    numberOfProducts - INITIALLY_VISIBLE_AMOUNT,
  );
  // Available amount is the amount of products based on current filters
  const [availableCount, setAvailableCount] =
    useState<number>(numberOfProducts);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  useEffect(() => {
    const selectedCreditTierValue = creditTiers.find(
      (tier) => tier.label === selectedCreditTier,
    )?.value;

    const filteredProducts = productsWithCost
      // Filter products by credit cost
      .filter(({ creditCost }) => {
        if (selectedCreditTier === 'Any') {
          return true;
        }

        return creditCost === selectedCreditTierValue;
      })
      // Filter products by category
      .filter(({ slug }) => {
        if (selectedCategory === 'All') {
          return true;
        }

        const product =
          productsWithSpreeData && slug && productsWithSpreeData[slug];

        return (
          product &&
          (product?.categories?.includes(selectedCategory) ||
            product?.category === selectedCategory)
        );
      });

    // Update the available amount
    setAvailableCount(filteredProducts.length);

    // Update the visible products
    setVisibleProducts(
      filteredProducts
        // If expanded is true show all products else show the first 8
        .slice(
          0,
          isExpanded ? productsWithCost.length : INITIALLY_VISIBLE_AMOUNT,
        ),
    );

    // Update the remaining amount
    setRemainingCount(
      filteredProducts.length - (isExpanded ? 0 : INITIALLY_VISIBLE_AMOUNT),
    );
  }, [
    creditTiers,
    productsWithCost,
    selectedCreditTier,
    selectedCategory,
    productsWithSpreeData,
    isExpanded,
    numberOfProducts,
  ]);

  const handleCategoryChange = (selectedCategory: string) => {
    setSelectedCategory(selectedCategory);

    ewAnalytics.track({
      event: ANALYTICS.MDP.CATEGORY_FILTER_CHANGE,
      data: {
        categorySelected: selectedCategory,
        tierSelected: selectedCreditTier,
      },
    });
  };

  const handleCreditTierChange = (selectedCreditTier: string) => {
    setSelectedCreditTier(selectedCreditTier);

    ewAnalytics.track({
      event: ANALYTICS.MDP.TIER_FILTER_CHANGE,
      data: {
        categorySelected: selectedCategory,
        tierSelected: selectedCreditTier,
      },
    });
  };

  const handleShowMore = () => {
    setIsExpanded(!isExpanded);

    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        tierSelected: selectedCreditTier,
        categorySelected: selectedCategory,
        label: isExpanded ? 'Show less' : 'Show all',
      },
    });
  };

  const lDFlags = useFlags();
  const priceDisplayTestEnabled = lDFlags.everlywellPriceDisplayTest;

  return (
    <SectionContainer
      size={['fluid', 'fluid', 'fluid', 'xl']}
      paddingX={[0, 4, 6, 8, 0]}
      alignSelf="center"
      centerContent
      as="section"
    >
      <Box paddingX="4" marginBottom={[8, 10]} width={['100%', '100%', '70%']}>
        {displayName ? (
          <Text
            fontSize={['md', 'lg']}
            color="viridian.base"
            textAlign="center"
            marginBottom={['1', '2']}
          >
            {displayName}
          </Text>
        ) : null}

        {title ? (
          <Heading
            as="h2"
            // @ts-ignore
            size={['32px', '2xl']}
            textAlign="center"
            marginBottom={[4, 3]}
          >
            {title}
          </Heading>
        ) : null}

        {description?.description ? (
          <Text
            fontSize={['md', 'lg']}
            color={theme.colors.tints.darkGrey}
            textAlign="center"
          >
            {description.description}
          </Text>
        ) : null}
      </Box>

      <Box
        display="flex"
        gap={[3, 4]}
        width="100%"
        paddingX="4"
        paddingBottom={[3, 5]}
      >
        {categories ? (
          <DropdownFilter
            buttonLabel="Category"
            options={categories}
            defaultSelectedLabel={categories[0]}
            onChange={(selected) => handleCategoryChange(selected[0] || 'All')}
          />
        ) : null}
        {creditTiers ? (
          <DropdownFilter
            buttonLabel="Credits"
            options={creditLabels}
            defaultSelectedLabel={creditLabels[0]}
            onChange={(selected) =>
              handleCreditTierChange(selected[0] || 'Any')
            }
          ></DropdownFilter>
        ) : null}
      </Box>

      <Box padding={[4, null, 0]}>
        {productsWithSpreeData && visibleProducts?.length ? (
          <Grid columns={[1, 2, 3]}>
            {visibleProducts?.map(
              ({ slug, creditCost, tag, tagVariant }, index) => {
                const product = slug && productsWithSpreeData[slug];

                if (!product) {
                  return null;
                }

                const pdpLink = `/products/${slug}/`;
                const price = product?.price;

                const dollarAmount = price ? Math.floor(price) : 0;

                return (
                  // First item of the grid is the featured product and wider on larger screens
                  <GridItem
                    key={`grid-item-${index}`}
                    width={index === 0 ? [1, 1, 2 / 3] : [1, 1, 1 / 3]}
                  >
                    <ProductCardV2
                      key={slug}
                      tag={
                        <Box display={['none', 'block']}>
                          {tag ? (
                            <Tag
                              size="sm"
                              variant={
                                tagVariant && tagVariant.length
                                  ? (tagVariant as TagProps['variant'])
                                  : 'primary'
                              }
                            >
                              {tag}
                            </Tag>
                          ) : null}
                        </Box>
                      }
                      variant={index === 0 ? 'featured' : 'default'}
                      image={
                        product?.productCardImage?.gatsbyImageData ? (
                          <GatsbyImage
                            image={product?.productCardImage?.gatsbyImageData}
                            alt={`${product?.displayName} box image`}
                            imgStyle={{ objectFit: 'contain' }}
                            style={{ height: '100%' }}
                          ></GatsbyImage>
                        ) : null
                      }
                      pricing={
                        <>
                          <Text
                            fontSize="lg"
                            color={theme.colors.tints.black}
                            fontWeight={'medium'}
                            marginRight="2"
                          >
                            {creditCost} Credit
                            {creditCost && creditCost > 1 ? 's' : ''}
                            {creditCost && priceDisplayTestEnabled
                              ? ` ($${creditCost * 39})`
                              : null}
                          </Text>
                          <PriceV2
                            dollarAmount={dollarAmount}
                            variant="strikethrough"
                          ></PriceV2>
                        </>
                      }
                      cta={
                        hasPdpNavigation ? (
                          <Button
                            colorScheme="whiteCS"
                            size="sm"
                            as={Link}
                            to={pdpLink}
                          >
                            View
                          </Button>
                        ) : undefined
                      }
                      linkTo={hasPdpNavigation ? pdpLink : undefined}
                    >
                      {tag ? (
                        <Box display={['block', 'none']} marginBottom="2">
                          {tag ? (
                            <Tag
                              size="sm"
                              variant={
                                tagVariant && tagVariant.length
                                  ? (tagVariant as TagProps['variant'])
                                  : 'primary'
                              }
                            >
                              {tag}
                            </Tag>
                          ) : null}
                        </Box>
                      ) : null}

                      <Text
                        fontSize={['lg', '3xl']}
                        color="black"
                        fontWeight="medium"
                        marginBottom={0.5}
                        textAlign={['left', 'center']}
                      >
                        {product.displayName}
                      </Text>
                      <Text
                        fontSize="sm"
                        color={theme.colors.tints.darkGrey}
                        textAlign={['left', 'center']}
                      >
                        {product.shortDescription}
                      </Text>
                    </ProductCardV2>
                  </GridItem>
                );
              },
            )}
          </Grid>
        ) : null}
      </Box>

      {visibleProducts.length === 0 ? (
        <Box paddingX="4" width="100%" display="flex">
          <Box
            backgroundColor={theme.colors.tints.lightGrey}
            height={[300, 400]}
            width="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            borderRadius="lg"
          >
            <Text fontSize={['lg', 'xl']}>No products match your filters</Text>
          </Box>
        </Box>
      ) : null}

      {remainingCount > 0 ? (
        <Box
          width={['100%', '100%', 'auto']}
          marginTop={[2, 2, 4]}
          paddingX={[4, 4, 0]}
        >
          <Button
            variant="secondary"
            onClick={handleShowMore}
            marginRight={[2, 4]}
            width={['100%', '100%', 'auto']}
          >
            {isExpanded ? 'Show less' : `Show all (${availableCount})`}
          </Button>
        </Box>
      ) : null}
    </SectionContainer>
  );
}
